import {Component, HostBinding, Inject, OnInit, ViewChild} from '@angular/core';
import {ModalToolContainerService, WebmateToolConfig} from "../modaltoolcontainer/modaltoolcontainer.service";
import {TOOL_CONTAINER_DATA} from "../modaltoolcontainer/modaltoolcontainer.component";
import {ScreenshotId, ImageUrl, ProjectId} from '@webmate-web-core/shared/types/ids';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {StateInfoItem} from "@webmate-web-core/shared/components/state/stateinfoitemrendererfactory";
import {faAdjust, faCompress, faDownload, faEdit, faExpand, faEye} from '@fortawesome/pro-solid-svg-icons';
import {of} from 'rxjs';
import {finalize, take} from "rxjs/operators";
import {ContrastAnalysisService} from "@webmate-web-core/shared/services/contrast-analysis.service";
import {
    ContrastAnalysisViewComponent, ContrastAnalysisViewComponentConfig
} from "@webmate-web-core/shared/tools/contrast-analysis-view/contrast-analysis-view.component";
import {ImageEditorToolComponent, ImageEditorToolConfig} from "@webmate-web-core/shared/tools/screenshotviewer/image-editor.component";
import {ImageMetadata} from '@webmate-web-core/shared/api/rest/image.restapi';
import {ImageComponent} from '@webmate-web-core/shared/components/screenshot/image.component';
import {UntilDestroy} from '@ngneat/until-destroy';
import {isScrollableImage$} from '@webmate-web-core/shared/utils/image-scrollable';

export class ScreenshotViewerToolConfig implements WebmateToolConfig {
    toolName = "Image Viewer";

    /**
     * Screenshot viewer can be used to show an image from webmate image service or from a given image URL (such as a data URL).
     */
    constructor(
        public readonly imageId: ScreenshotId | undefined,
        public readonly imageUrl?: ImageUrl,
        public readonly stateInfoItems?: StateInfoItem[],
        public readonly offerContrastAnalysis: boolean = false,
        public readonly insidePopup: boolean = true,
        public readonly projectId?: ProjectId,
        public readonly overlaysChangedCallback?: (meta: ImageMetadata) => void) {}
}

@UntilDestroy()
@Component({
    selector: "wm-screenshotviewer",
    templateUrl: "./screenshotviewer.component.html",
    styleUrls: ['./screenshotviewer.component.less'],
})
export class ScreenshotViewerComponent implements OnInit {

    @ViewChild('image') imageElement: ImageComponent;

    imageUrl?: ImageUrl;
    errorText: string | undefined;

    cover: boolean = true;

    analyzingContrast : boolean = false;
    contrastAnalysis: {analysis: any};

    childRecreateGuard: boolean = true;

    fitToContainer: boolean = false;
    shouldShowScrollViewBtn: boolean = false;

    @HostBinding('class.inside-popup') get getPopUpBasedStyle() {
        return this.data.insidePopup;
    }

    constructor(@Inject(TOOL_CONTAINER_DATA) public data: ScreenshotViewerToolConfig,
                private imageService: ImageService,
                private contrastAnalysisService: ContrastAnalysisService,
                private modalToolService: ModalToolContainerService) {
        if (!!data.imageId) {
            this.imageUrl = this.imageService.getImageUrl(data.imageId);
            if (!this.imageUrl) {
                this.errorText = 'Image could not be loaded. Image URL could not found';
            }

        } else if (!!data.imageUrl) {
            this.imageUrl = this.data.imageUrl;
        } else {
            this.errorText = 'Image could not be loaded. No image given.';
        }
    }

    ngOnInit() {
        if (this.imageUrl) {
            isScrollableImage$(this.imageUrl).subscribe(isScrollable => {
              this.shouldShowScrollViewBtn = isScrollable;
            });
        }
    }

    editScreenshot(event: MouseEvent) {
        if (!this.data.imageId) {
            console.error("No image id to edit the image");
            return;
        }
        if (!this.imageUrl) {
            return;
        }
        const self = this;
        this.modalToolService.openTool(
            ImageEditorToolComponent,
            new ImageEditorToolConfig(this.data.imageId!, this.imageUrl, this.data.projectId,  metadata => {
                this.data.overlaysChangedCallback?.(metadata);
                // recreate the image component to show the latest edits
                this.childRecreateGuard = false;
                setTimeout(() => {this.childRecreateGuard = true}, 0);
            })
        );
    }

    analyzeContrast(event: MouseEvent) {
        if (!this.data.imageUrl && !this.data.imageId) {
            console.error("Missing image information to analyze contrast");
            return;
        }
        const imageUrl = this.data.imageUrl || this.imageService.getImageUrl(this.data.imageId!);
        if (this.data.offerContrastAnalysis && this.data.projectId && (this.data.imageId || this.data.imageUrl)) {
            if (this.analyzingContrast) return;
            this.analyzingContrast = true;
            this.contrastAnalysisService.analyzeContrast$(this.data.projectId!!, this.data.imageId, of(imageUrl)).pipe(
                finalize(() => this.analyzingContrast = false),
                take(1)
            ).subscribe(({analysis}) => {
                this.contrastAnalysis = analysis;
                this.modalToolService.openTool(
                    ContrastAnalysisViewComponent,
                    new ContrastAnalysisViewComponentConfig(imageUrl, this.contrastAnalysis)
                );
            });
        } else {
            throw new Error("Missing required (projectId, imageId/imageUrl) to offer contrast analysis");
        }
    }

    downloadScreenshot(_: MouseEvent) {
        this.imageService.requestDownload({
            name: 'screenshot',
            imageUrl: this.imageUrl,
            imageId: this.data.imageId,
        });
    }

    fitWholeImage() {
        this.fitToContainer = true;
    }

    goToScrollView() {
        this.fitToContainer = false;
    }


    protected readonly confirm = confirm;
    protected readonly faAdjust = faAdjust;
    protected readonly faDownload = faDownload;
    protected readonly faEdit = faEdit;
    protected readonly faEye = faEye;
    protected readonly faExpand = faExpand;
    protected readonly faCompress = faCompress;
}
